import {
  MutationFunction,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { useCallback } from 'react';

import { coreLoginClient } from 'app/api/apiClient/generated/coreLoginClient';
import { CoreApiError } from 'app/api/hooks/core/v1/errors';
import { useAuthDispatch } from 'app/state/AuthContext/hooks';
import { invalidateQueries } from 'app/utils/lib/react-query';

// eslint-disable-next-line @stylistic/max-len
export interface EnhancedMutationConfig<TResult = unknown, TError = unknown, TVariables = unknown, TSnapshot = unknown> extends UseMutationOptions<TResult, TError, TVariables, TSnapshot> {
  invalidateTableQueryPrefixes?: string[];
}

export function useMutationEnhanced<
  TResult extends { data: unknown },
  TError = unknown,
  TVariables = void,
  TSnapshot = unknown,
>(mutationFn: MutationFunction<TResult, TVariables>,
  {
    invalidateTableQueryPrefixes,
    ...config
  }: EnhancedMutationConfig<
    TResult['data'],
    TError,
    TVariables,
    TSnapshot
  > = {}): UseMutationResult<TResult['data'], TError, TVariables, TSnapshot> {
  const queryClient = useQueryClient();
  const dispatch = useAuthDispatch();

  const mutation = useCallback(async (variables: TVariables) => {
    try {
      const { data } = await mutationFn.apply(null,
        [variables]) as AxiosResponse<TResult>;

      return data;
    } catch (error) {
      if ((error as CoreApiError<TError>).status === 401) {
        try {
          const { data } = await coreLoginClient.newToken();
          dispatch({
            payload: {
              token: data.token,
            },
            type: 'setLoginInfo',
          });
        } catch {
          window.location.reload();
        }
      } else {
        throw error;
      }
    }
  },
  [dispatch, mutationFn]);

  const {
    onError,
    ...options
  } = config;

  return useMutation<TResult['data'], TError, TVariables, TSnapshot>(mutation,
    {
      ...options,
      ...(onError ? { onError } : {}),
      onSuccess: (...onSuccessArgs) => {
        config?.onSuccess?.(...onSuccessArgs);

        if (invalidateTableQueryPrefixes) {
          invalidateQueries({
            invalidateQueryPredicates: invalidateTableQueryPrefixes,
            queryClient,
          });
        }
      },
    });
}
